<template>
  <div class="flex-col gap-y-[12px]">
    <b-form-group :label="!hideLabel ? 'Icon' : ''">
      <v-select
        v-model="model"
        :filterable="false"
        :options="paginated"
        placeholder="None"
        searchable
        @close="onClose"
        @open="onOpen"
        @search="(query) => (search = query)"
      >
        <template #selected-option="option">
          <span v-if="option.label === 'custom'">Custom</span>
          <i
            v-else
            :class="option.label"
          />
        </template>

        <template #option="option">
          <span v-if="option.label === 'custom'">Custom Icon</span>
          <i
            v-else
            :class="option.label"
          />
        </template>

        <template #list-footer>
          <li
            v-show="hasNextPage"
            ref="load"
            class="loader"
          >
            Loading more options...
          </li>
        </template>
      </v-select>
    </b-form-group>

    <upload-asset
      v-if="value === 'custom'"
      title="Upload Icon"
      type="svg,png,jpg,jpeg,gif,webp"
      @uploaded="uploaded"
    />
  </div>
</template>

<script>
import UploadAsset
  from '@/components/editor/components/sid-controls/sid-control-pressets/background-image/UploadAsset.vue'
import iconMetadata from '@fortawesome/fontawesome-free/metadata/icon-families.json'

const STYLE_PREFIX_MAP = {
  brands: 'fab',
  regular: 'far',
  solid: 'fas'
}

const FREE_ICONS = Array.from(
  new Set(
    Object.entries(iconMetadata)
      .flatMap(([iconName, metadata]) => (metadata.familyStylesByLicense?.free || [])
        .map(({style}) => STYLE_PREFIX_MAP[style])
        .filter(Boolean)
        .map((prefix) => `${prefix} fa-${iconName}`)
      )
  )
).sort((a, b) => a.localeCompare(b))

export default {
  name: 'IconSelect',
  components: {UploadAsset},
  props: {
    value: {
      required: true
    },
    section: {
      required: false
    },
    showCustom: {
      type: Boolean,
      default: false
    },
    hideLabel: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    observer: null,
    limit: 10,
    search: '',
    icons: ['custom', ...FREE_ICONS]
  }),
  computed: {
    model: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
      }
    },
    filtered () {
      const q = this.search.toLowerCase()

      const getLabel = (opt) => (typeof opt === 'string' ? opt : opt?.label)

      const base = this.showCustom
        ? this.icons
        : this.icons.filter((opt) => getLabel(opt) !== 'custom')

      return base.filter((opt) => (getLabel(opt) || '').toLowerCase().includes(q))
    },
    paginated () {
      return this.filtered.slice(0, this.limit)
    },
    hasNextPage () {
      return this.paginated.length < this.filtered.length
    }
  },
  mounted () {
    this.observer = new IntersectionObserver(this.infiniteScroll)
  },
  methods: {
    async uploaded (file) {
      try {
        if (!this.section) console.error('Section not found')
        const ext = (file.url || '').split('.').pop().toLowerCase().split('?')[0]

        if (ext === 'svg') {
          const response = await fetch(file.url)
          const svg = await response.text()
          this.$set(this.section.options.content, 'svg', svg)
          this.$set(this.section.options.content, 'iconUrl', null)
        } else {
          this.$set(this.section.options.content, 'iconUrl', file.url)
          this.$set(this.section.options.content, 'svg', null)
        }
      } catch (error) {
        console.error('Failed to fetch icon:', error)
      }
    },
    async onOpen () {
      if (this.hasNextPage) {
        await this.$nextTick()
        this.observer.observe(this.$refs.load)
      }
    },
    onClose () {
      this.limit = 10
      this.observer.disconnect()
    },
    async infiniteScroll ([{isIntersecting, target}]) {
      if (isIntersecting) {
        const ul = target.offsetParent
        const scrollTop = target.offsetParent.scrollTop
        this.limit += 10
        await this.$nextTick()
        ul.scrollTop = scrollTop
      }
    }
  }
}
</script>
